import { ReactNode, useEffect, useRef, useState, HTMLAttributes } from "react";

interface LazyOnVisibleProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  children: ReactNode;
  /** Min height reserved before content mounts (prevents CLS) */
  minHeight?: string;
  /** rootMargin for IntersectionObserver (preload distance) */
  rootMargin?: string;
  /** If true, mount immediately on idle even if not visible (for above-fold-adjacent sections) */
  mountOnIdle?: boolean;
}

/**
 * Defers mounting children until they're about to enter the viewport.
 * Reduces initial JS execution / TBT by avoiding render of non-critical sections.
 */
export default function LazyOnVisible({
  children,
  minHeight = "200px",
  rootMargin = "400px",
  mountOnIdle = false,
  ...rest
}: LazyOnVisibleProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(node);

    let idleId: number | undefined;
    if (mountOnIdle && typeof window !== "undefined") {
      const ric = (window as any).requestIdleCallback as
        | ((cb: () => void, opts?: { timeout: number }) => number)
        | undefined;
      if (ric) {
        idleId = ric(() => setVisible(true), { timeout: 2500 });
      } else {
        idleId = window.setTimeout(() => setVisible(true), 1500) as unknown as number;
      }
    }

    return () => {
      io.disconnect();
      if (idleId !== undefined) {
        const cic = (window as any).cancelIdleCallback as ((id: number) => void) | undefined;
        if (cic) cic(idleId);
        else clearTimeout(idleId);
      }
    };
  }, [visible, rootMargin, mountOnIdle]);

  return (
    <div ref={ref} style={{ minHeight: visible ? undefined : minHeight }} {...rest}>
      {visible ? children : null}
    </div>
  );
}
